import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { GlobalApi, ProvisionApi } from '@Apis';
import CSpinner from 'src/components/CSpinner';
import { PRODUCT_NAME } from 'src/utils/const';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { useSiderWidthStore } from 'src/models/useSiderWidthModel';
import { useOrgStore } from 'src/models/useOrgModel';
import { PolicyType } from 'src/types/app';
import { GrantedAccess } from '../Common/GrantedAccess';
import DeployAddInBanner from './DeployAddInBanner';
import AdminConsentBanner from './AdminConsentBanner';

type MisdirectedEmailAction = 'prompt' | 'pass';

interface MisdirectedEmailDetectionSettings {
  enabled: boolean;
  action: MisdirectedEmailAction;
}

const DEFAULT_SETTINGS: MisdirectedEmailDetectionSettings = {
  enabled: false,
  action: 'prompt',
};

const MisdirectedEmailDetection = React.memo(() => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { siderWidth } = useSiderWidthStore();
  const { orgId } = useOrgStore();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isProvisioned, setIsProvisioned] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState('');

  const [savedSettings, setSavedSettings] =
    useState<MisdirectedEmailDetectionSettings>(DEFAULT_SETTINGS);
  const [settings, setSettings] =
    useState<MisdirectedEmailDetectionSettings>(DEFAULT_SETTINGS);

  const isDirty =
    settings.enabled !== savedSettings.enabled ||
    settings.action !== savedSettings.action;

  useEffect(() => {
    loadData();
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      // Misdirected Email Detection rides on the Exchange Online provision —
      // without it, the BFF settings endpoint cannot resolve a tenant context
      // and Outlook Add-in deployment is moot. Mirror ExchangeOnlineApiProtection's
      // gate so users land on the same GrantedAccess CTA they already know.
      const policyTypes = (
        await ProvisionApi.getProvisionedServicesByOrgId(orgId)
      ).data;
      if (!policyTypes.includes(PolicyType.Exchange)) {
        setIsProvisioned(false);
        setSelectedPolicyType(PolicyType.Exchange.toString());
        return;
      }
      setIsProvisioned(true);

      const res = await GlobalApi.getMisdirectedEmailDetectionSettings();
      const loaded: MisdirectedEmailDetectionSettings = {
        enabled: !!res.data?.enabled,
        action: res.data?.action ?? 'prompt',
      };
      setSavedSettings(loaded);
      setSettings(loaded);
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isDirty) return;
    try {
      setSaving(true);
      await GlobalApi.updateMisdirectedEmailDetectionSettings({
        enabled: settings.enabled,
        action: settings.action,
      });
      setSavedSettings({ ...settings });
      window.showNotification({
        titleMsg: t('globalSetting.misdirectedEmailDetection.saveSuccessToast'),
        severity: 'success',
      });
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings((prev) => ({ ...prev, enabled: e.target.checked }));
  };

  const handleActionChange = (value: string) => {
    setSettings((prev) => ({
      ...prev,
      action: value as MisdirectedEmailAction,
    }));
  };

  const canEdit = withEditPermission;

  return (
    <Box px="4x">
      {(loading || saving) && <CSpinner />}
      {isProvisioned ? (
        <Box pb="60px">
          <Text
            mt="28px"
            fontSize="xl"
            lineHeight="xl"
            color={colorStyle.color.primary}
          >
            {t('globalSetting.misdirectedEmailDetection.title')}
          </Text>
          <Text mt="4px" color={colorStyle.color.secondary} lineHeight="lg">
            {t('globalSetting.misdirectedEmailDetection.description')}
          </Text>

          {/*
          Banners go above the divider so they are visually grouped with the
          page header rather than the form. Order matters: deployment must
          happen before consent can be granted, so DeployAddInBanner is first.
        */}
          <DeployAddInBanner />
          {/*
          key={orgId} forces a fresh mount on org switch, so phase / pollTimer /
          modal / localStorage outcome are all reset and /status is re-fetched
          for the new org's tenant. DeployAddInBanner is per-browser (localStorage
          dismissed flag) and intentionally does not key on org.
        */}
          <AdminConsentBanner key={orgId} />

          <Divider my="6x" />

          <Box>
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.misdirectedEmailDetection.enabledLabel')}
            </Text>
            <Flex mt="3x" alignItems="center">
              <Switch
                disabled={!canEdit || loading}
                checked={settings.enabled}
                onChange={handleToggleChange}
              />
              <Text
                ml="2x"
                color={colorStyle.color.secondary}
                lineHeight="lg"
                maxWidth={900}
              >
                {t('globalSetting.misdirectedEmailDetection.enabledHelper', {
                  productName: PRODUCT_NAME,
                })}
              </Text>
            </Flex>
          </Box>

          <Box mt="8x">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.misdirectedEmailDetection.actionTitle')}
            </Text>
            <Text
              mt="3x"
              color={colorStyle.color.secondary}
              lineHeight="lg"
              maxWidth={1000}
            >
              {t('globalSetting.misdirectedEmailDetection.actionHelper')}
            </Text>

            <Box mt="4x">
              <RadioGroup value={settings.action} onChange={handleActionChange}>
                <Stack direction="column" spacing="4x">
                  <Box>
                    <Radio
                      name="prompt"
                      value="prompt"
                      disabled={!canEdit || !settings.enabled || loading}
                    >
                      <Text color={colorStyle.color.primary}>
                        {t(
                          'globalSetting.misdirectedEmailDetection.actionPromptLabel'
                        )}
                      </Text>
                    </Radio>
                    <Text
                      mt="1x"
                      ml="6x"
                      color={colorStyle.color.secondary}
                      fontSize="sm"
                      lineHeight="sm"
                      maxWidth={900}
                    >
                      {t(
                        'globalSetting.misdirectedEmailDetection.actionPromptHelper'
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Radio
                      name="pass"
                      value="pass"
                      disabled={!canEdit || !settings.enabled || loading}
                    >
                      <Text color={colorStyle.color.primary}>
                        {t(
                          'globalSetting.misdirectedEmailDetection.actionPassLabel'
                        )}
                      </Text>
                    </Radio>
                    <Text
                      mt="1x"
                      ml="6x"
                      color={colorStyle.color.secondary}
                      fontSize="sm"
                      lineHeight="sm"
                      maxWidth={900}
                    >
                      {t(
                        'globalSetting.misdirectedEmailDetection.actionPassHelper'
                      )}
                    </Text>
                  </Box>
                </Stack>
              </RadioGroup>
            </Box>
          </Box>

          <Flex mt="8x" mb="8x" alignItems="baseline" gap="2x">
            <Text fontWeight="semibold" flexShrink={0} whiteSpace="nowrap">
              {t('globalSetting.note.title')}
            </Text>
            <Text
              color={colorStyle.color.secondary}
              lineHeight="lg"
              maxWidth={1000}
            >
              {t('globalSetting.misdirectedEmailDetection.note')}
            </Text>
          </Flex>

          <Flex
            position="fixed"
            bottom={0}
            left={270 + siderWidth}
            right={0}
            height={64}
            alignItems="center"
            pl="5x"
            gap="2x"
            background={colorStyle.background.secondary}
          >
            <Button
              variant="primary"
              disabled={!canEdit || !isDirty || saving}
              onClick={handleSave}
            >
              {t('common.button.save')}
            </Button>
          </Flex>
        </Box>
      ) : null}

      {!isProvisioned && !loading ? (
        <GrantedAccess selectedPolicyType={selectedPolicyType} orgId={orgId} />
      ) : null}
    </Box>
  );
});

export default MisdirectedEmailDetection;
